interface CompressedLocation {
  lat: number;  // Arrondi à 4 décimales (précision ~11m)
  lng: number;
  acc?: number; // Précision GPS
  ts: number;   // Timestamp compressé (secondes)
  src: 'gps' | 'manual' | 'map';
  ttl: number;  // TTL en secondes
}

interface CachedAddress {
  query: string;
  result: {
    lat: number;
    lng: number;
    formatted: string;
    components: {
      city?: string;
      country?: string;
      postcode?: string;
    };
  };
  timestamp: number;
  ttl: number;
}

interface CachedHiddenEvent {
  eventId: string;
  seriesId?: string;
  reason: 'swipe' | 'favorite';
  timestamp: number;
}

class LocationCacheService {
  // OPTIMISATION : Pattern Singleton
  private static instance: LocationCacheService;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'EventConnectLocationCache';
  private readonly DB_VERSION = 9; // 🌍 GEOHASH v1 - Migration vers indexation spatiale geohash
  private readonly LOCATIONS_STORE = 'locations';
  private readonly ADDRESSES_STORE = 'addresses';
  // 🆕 SMART CACHE - 3 STORES DISTINCTS selon le plan de refactorisation
  private readonly GEO_EVENTS_STORE = 'geoEvents';        // Événements zone géographique
  private readonly ORGANIZER_EVENTS_STORE = 'organizerEvents'; // Événements organisateur  
  private readonly FAVORITE_EVENTS_STORE = 'favoriteEvents';   // Favoris locaux
  private readonly PUBLIC_ORGANIZERS_STORE = 'publicOrganizers'; // 🆕 Profils organisateurs publics
  
  // ✅ STORES ACTIFS COMPLÉMENTAIRES
  private readonly SERIES_STORE = 'series';        // Gestion séries d'événements récurrents
  private readonly HIDDEN_EVENTS_STORE = 'hiddenEvents';  // Événements masqués utilisateur
  
  // 🗑️ DEPRECATED CACHES - Plus utilisés avec le Smart Cache
  // private favoritesCache = new Map<string, boolean>();
  // private requestCache = new Map<string, Promise<any>>();

  // OPTIMISATION : Pattern Singleton
  static getInstance(): LocationCacheService {
    if (!LocationCacheService.instance) {
      LocationCacheService.instance = new LocationCacheService();
      // Auto-initialisation du singleton pour éviter les multiples init()
      LocationCacheService.instance.init().catch(console.error);
    }
    return LocationCacheService.instance;
  }

  // Getter pour vérifier l'état d'initialisation
  get isInitialized(): boolean {
    return this.initialized;
  }

  // OPTIMISATION : Éviter les initialisations multiples + race conditions
  async init(): Promise<void> {
    if (this.initialized) return;

    // Si une initialisation est déjà en cours, réutiliser la même promesse
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInit().catch((err) => {
      // Reset pour permettre un retry en cas d'échec
      this.initPromise = null;
      throw err;
    });
    return this.initPromise;
  }

  private _doInit(): Promise<void> {
    console.log('🔧 Initialisation du LocationCacheService...');

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error('❌ Erreur lors de l\'initialisation IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.initialized = true;

        // 🍎 SAFARI/iOS - La connexion IndexedDB est fermée quand l'app passe en
        // arrière-plan ou que l'écran se verrouille. On réinitialise alors l'état
        // pour que le prochain appel de cache rouvre une connexion fraîche
        // (sinon `this.db` pointe vers une base fermée mais non-null → toutes les
        // transactions lèvent `InvalidStateError` jusqu'au rechargement de la page).
        this.db.onclose = () => {
          console.warn('⚠️ [IndexedDB] Connexion fermée (Safari arrière-plan ?), réinitialisation de l\'état');
          this.db = null;
          this.initialized = false;
          this.initPromise = null;
        };
        // Une autre connexion demande une montée de version (ex: nouveau déploiement
        // qui bump DB_VERSION) : on libère la nôtre proprement pour ne pas bloquer la
        // mise à niveau, et on réinitialise l'état (close() ne déclenche pas onclose).
        this.db.onversionchange = () => {
          console.warn('⚠️ [IndexedDB] versionchange détecté, fermeture de la connexion');
          this.db?.close();
          this.db = null;
          this.initialized = false;
          this.initPromise = null;
        };

        console.log('✅ LocationCacheService initialisé avec succès');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        console.log('🔄 Mise à jour de la base IndexedDB...');
        const db = (event.target as IDBOpenDBRequest).result;
        console.log('🔄 Mise à jour IndexedDB vers la version', this.DB_VERSION);

        // Créer les stores existants s'ils n'existent pas
        if (!db.objectStoreNames.contains(this.LOCATIONS_STORE)) {
          console.log('📦 Création du store locations...');
          const locationStore = db.createObjectStore(this.LOCATIONS_STORE, { keyPath: 'id' });
          locationStore.createIndex('timestamp', 'timestamp', { unique: false });
          locationStore.createIndex('source', 'source', { unique: false });
        }

        if (!db.objectStoreNames.contains(this.ADDRESSES_STORE)) {
          console.log('📦 Création du store addresses...');
          const addressStore = db.createObjectStore(this.ADDRESSES_STORE, { keyPath: 'query' });
          addressStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // 🗑️ STORES LEGACY SUPPRIMÉS - App pas encore déployée, pas de migration nécessaire
        // - zones → remplacé par geoEvents (Smart Cache)
        // - events → remplacé par geoEvents + organizerEvents (Smart Cache)  
        // - favorites → remplacé par favoriteEvents (Smart Cache)
        // - organizers → remplacé par publicOrganizers (Smart Cache)

        // ✅ Ajouter le store hiddenEvents
        if (!db.objectStoreNames.contains(this.HIDDEN_EVENTS_STORE)) {
          const hiddenEventsStore = db.createObjectStore(this.HIDDEN_EVENTS_STORE, { keyPath: 'eventId' });
          hiddenEventsStore.createIndex('timestamp', 'timestamp', { unique: false });
          hiddenEventsStore.createIndex('reason', 'reason', { unique: false });
          console.log('📦 Store hiddenEvents créé');
        }

        // 🆕 SMART CACHE - Créer les 3 nouveaux stores distincts
        if (!db.objectStoreNames.contains(this.GEO_EVENTS_STORE)) {
          console.log('📦 Création du store geoEvents...');
          const geoEventsStore = db.createObjectStore(this.GEO_EVENTS_STORE, { keyPath: 'id' });
          geoEventsStore.createIndex('timestamp', 'timestamp', { unique: false });
          geoEventsStore.createIndex('zoneKey', 'zoneKey', { unique: false });
          geoEventsStore.createIndex('geohash', 'geohash', { unique: false }); // 🌍 GEOHASH - Index pour recherche spatiale
          geoEventsStore.createIndex('endTime', 'endTime', { unique: false });
          geoEventsStore.createIndex('latitude', 'latitude', { unique: false });
          geoEventsStore.createIndex('longitude', 'longitude', { unique: false });
          console.log('✅ Store geoEvents créé avec index géographiques + geohash');
        }

        // 🌍 GEOHASH MIGRATION - Ajouter index geohash si store existe déjà (upgrade v8 → v9)
        if (db.objectStoreNames.contains(this.GEO_EVENTS_STORE)) {
          const transaction = (event.target as IDBOpenDBRequest).transaction;
          if (transaction) {
            const geoEventsStore = transaction.objectStore(this.GEO_EVENTS_STORE);
            if (!geoEventsStore.indexNames.contains('geohash')) {
              console.log('🔄 Migration: Ajout index geohash sur geoEvents...');
              geoEventsStore.createIndex('geohash', 'geohash', { unique: false });
              console.log('✅ Index geohash ajouté');
            }
          }
        }

        if (!db.objectStoreNames.contains(this.ORGANIZER_EVENTS_STORE)) {
          console.log('📦 Création du store organizerEvents...');
          const organizerEventsStore = db.createObjectStore(this.ORGANIZER_EVENTS_STORE, { keyPath: 'id' });
          organizerEventsStore.createIndex('timestamp', 'timestamp', { unique: false });
          organizerEventsStore.createIndex('organizerId', 'organizerId', { unique: false });
          organizerEventsStore.createIndex('status', 'status', { unique: false });
          organizerEventsStore.createIndex('datetime', 'datetime', { unique: false });
          organizerEventsStore.createIndex('endTime', 'endTime', { unique: false });
          console.log('✅ Store organizerEvents créé avec index organisateur');
        }

        if (!db.objectStoreNames.contains(this.FAVORITE_EVENTS_STORE)) {
          console.log('📦 Création du store favoriteEvents...');
          const favoriteEventsStore = db.createObjectStore(this.FAVORITE_EVENTS_STORE, { keyPath: 'id' });
          favoriteEventsStore.createIndex('timestamp', 'timestamp', { unique: false });
          favoriteEventsStore.createIndex('endTime', 'endTime', { unique: false });
          favoriteEventsStore.createIndex('isFavorite', 'isFavorite', { unique: false });
          console.log('✅ Store favoriteEvents créé avec index favoris');
        }

        // ✅ STORE SERIES - Gestion des séries d'événements récurrents
        if (!db.objectStoreNames.contains(this.SERIES_STORE)) {
          console.log('📦 Création du store series...');
          const seriesStore = db.createObjectStore(this.SERIES_STORE, { keyPath: 'id' });
          seriesStore.createIndex('timestamp', 'timestamp', { unique: false });
          seriesStore.createIndex('endDate', 'endDate', { unique: false });
          seriesStore.createIndex('organizerId', 'organizerId', { unique: false });
          console.log('✅ Store series créé avec index séries');
        }

        // 🆕 STORE PUBLIC ORGANIZERS - Pour les profils publics des organisateurs
        if (!db.objectStoreNames.contains(this.PUBLIC_ORGANIZERS_STORE)) {
          console.log('📦 Création du store publicOrganizers...');
          const publicOrganizersStore = db.createObjectStore(this.PUBLIC_ORGANIZERS_STORE, { keyPath: 'id' });
          publicOrganizersStore.createIndex('timestamp', 'timestamp', { unique: false });
          publicOrganizersStore.createIndex('expiresAt', 'expiresAt', { unique: false });
          publicOrganizersStore.createIndex('organizer', 'organizer', { unique: false });
          console.log('✅ Store publicOrganizers créé avec index organisateur');
        }
      };
    });
  }

  /**
   * 🔒 Ouvre une transaction IndexedDB de façon résiliente.
   *
   * Sur Safari/iOS, la connexion IndexedDB peut se fermer sous nos pieds (app en
   * arrière-plan, verrouillage écran). Dans ce cas `this.db.transaction()` lève un
   * `InvalidStateError` (« The database connection is closing »). On détecte ce cas
   * précis, on réinitialise l'état et on rouvre une connexion avant de réessayer
   * UNE fois. Toute autre erreur est propagée telle quelle.
   *
   * Tous les accès aux stores passent par ce helper : c'est le point unique qui
   * empêche ces rejets de promesse non gérés de remonter jusqu'à Sentry et, surtout,
   * qui évite qu'un touriste sur iPhone voie une liste vide après avoir déverrouillé
   * son téléphone.
   */
  private async getTransaction(
    storeNames: string[],
    mode: IDBTransactionMode
  ): Promise<IDBTransaction> {
    try {
      if (!this.db) await this.init();
      return this.db!.transaction(storeNames, mode);
    } catch (error) {
      // - InvalidStateError : connexion fermée par Safari (arrière-plan, verrouillage)
      // - UnknownError « Database deleted by request of the user » : Safari/iOS a
      //   supprimé la base (éviction ITP 7 jours, pression stockage, webview in-app).
      //   Rouvrir recrée une base vide via onupgradeneeded — comportement voulu
      //   pour un cache : on repart de zéro plutôt que de faire planter l'app.
      if (
        error instanceof DOMException &&
        (error.name === 'InvalidStateError' || error.name === 'UnknownError')
      ) {
        console.warn(`⚠️ [IndexedDB] ${error.name} (connexion fermée ou base supprimée), réouverture puis nouvel essai...`);
        this.db = null;
        this.initialized = false;
        this.initPromise = null;
        await this.init();
        return this.db!.transaction(storeNames, mode);
      }
      throw error;
    }
  }





  // Compression des coordonnées
  compressLocation(location: {
    latitude: number;
    longitude: number;
    accuracy?: number;
    timestamp: number;
    source: 'gps' | 'manual' | 'map';
  }): CompressedLocation {
    return {
      lat: Math.round(location.latitude * 10000) / 10000,  // Précision ~11m
      lng: Math.round(location.longitude * 10000) / 10000,
      acc: location.accuracy,
      ts: Math.floor(location.timestamp / 1000), // Secondes au lieu de millisecondes
      src: location.source,
      ttl: this.getTTL(location.source)
    };
  }

  // TTL variable selon la source
  private getTTL(source: 'gps' | 'manual' | 'map'): number {
    switch (source) {
      case 'gps': return 3600;      // 1 heure
      case 'manual': return 86400;  // 24 heures
      case 'map': return 7200;      // 2 heures
      default: return 3600;
    }
  }


  // Sauvegarder une localisation
  async saveLocation(location: CompressedLocation): Promise<void> {
    console.log('💾 Sauvegarde de la localisation:', location);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.LOCATIONS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.LOCATIONS_STORE);

      const request = store.put({
        id: 'current',
        ...location,
        timestamp: Date.now()
      });

      request.onsuccess = () => {
        console.log('✅ Localisation sauvegardée avec succès');
        resolve();
      };
      request.onerror = () => {
        console.error('❌ Erreur lors de la sauvegarde:', request.error);
        reject(request.error);
      };
    });
  }

  // Récupérer la localisation actuelle
  async getCurrentLocation(): Promise<CompressedLocation | null> {
    console.log('🔍 Recherche de la localisation en cache...');
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.LOCATIONS_STORE], 'readonly');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.LOCATIONS_STORE);
      const request = store.get('current');

      request.onsuccess = () => {
        const cached = request.result;
        if (!cached) {
          console.log('❌ Aucune localisation trouvée en cache');
          resolve(null);
          return;
        }

        const now = Math.floor(Date.now() / 1000);
        const age = now - cached.ts;
        
        console.log(`📊 Cache trouvé - Âge: ${age}s, TTL: ${cached.ttl}s`);
        
        if (age < cached.ttl) {
          console.log('✅ Cache valide, utilisation de la localisation en cache');
          resolve(cached);
        } else {
          console.log('⏰ Cache expiré, suppression...');
          this.clearLocation().catch(() => {});
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('❌ Erreur lors de la récupération:', request.error);
        reject(request.error);
      };
    });
  }

  // Sauvegarder une adresse géocodée
  async saveAddress(query: string, result: CachedAddress['result']): Promise<void> {
    console.log('💾 Sauvegarde de l\'adresse:', query);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.ADDRESSES_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.ADDRESSES_STORE);

      const request = store.put({
        query: query.toLowerCase(),
        result,
        timestamp: Date.now(),
        ttl: 21600 // 6 heures pour les adresses
      });

      request.onsuccess = () => {
        console.log('✅ Adresse sauvegardée avec succès');
        resolve();
      };
      request.onerror = () => {
        console.error('❌ Erreur lors de la sauvegarde de l\'adresse:', request.error);
        reject(request.error);
      };
    });
  }

  // Récupérer une adresse géocodée
  async getAddress(query: string): Promise<CachedAddress['result'] | null> {
    console.log('🔍 Recherche de l\'adresse en cache:', query);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.ADDRESSES_STORE], 'readonly');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.ADDRESSES_STORE);
      const request = store.get(query.toLowerCase());

      request.onsuccess = () => {
        const cached = request.result;
        if (!cached) {
          console.log('❌ Adresse non trouvée en cache');
          resolve(null);
          return;
        }

        const now = Date.now();
        const age = now - cached.timestamp;
        
        console.log(`📊 Adresse trouvée - Âge: ${Math.floor(age/1000)}s, TTL: ${cached.ttl}s`);
        
        if (age < cached.ttl * 1000) {
          console.log('✅ Adresse en cache valide');
          resolve(cached.result);
        } else {
          console.log('⏰ Adresse en cache expirée, suppression...');
          this.clearAddress(query).catch(() => {});
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('❌ Erreur lors de la récupération de l\'adresse:', request.error);
        reject(request.error);
      };
    });
  }

  // 🚀 NETTOYAGE INTELLIGENT - Seulement si nécessaire
  async cleanup(): Promise<void> {
    if (!this.db) await this.init();

    const now = Date.now();
    const nowSeconds = Math.floor(now / 1000);

    // 🎯 VÉRIFICATION PRÉALABLE - Est-ce que le nettoyage est nécessaire ?
    const needsCleanup = await this.checkIfCleanupNeeded(nowSeconds);
    
    if (!needsCleanup) {
      // Nettoyage pas nécessaire - économiser les ressources
      return;
    }

    console.log('🧹 Nettoyage des données expirées...');
    let cleanedCount = 0;

    // Nettoyer seulement les stores qui existent et ont des données expirées
    const stores = [
      { name: this.LOCATIONS_STORE, checkFn: (item: any) => item.ts && (nowSeconds - item.ts) > (item.ttl || 3600) },
      { name: this.ADDRESSES_STORE, checkFn: (item: any) => item.timestamp && (now - item.timestamp) > ((item.ttl || 3600) * 1000) }
    ];

    // 🎯 VÉRIFIER L'EXISTENCE DES STORES AVANT NETTOYAGE
    const availableStoreNames = Array.from(this.db!.objectStoreNames);
    
    for (const store of stores) {
      if (availableStoreNames.includes(store.name)) {
        try {
          const cleaned = await this.cleanupStore(store.name, store.checkFn);
          cleanedCount += cleaned;
        } catch (error) {
          console.warn(`⚠️ Erreur nettoyage store ${store.name}:`, error);
        }
      } else {
        console.log(`ℹ️ Store ${store.name} n'existe pas, ignoré`);
      }
    }

    console.log(`✅ Nettoyage terminé: ${cleanedCount} éléments supprimés`);
  }

  /**
   * 🎯 NOUVEAU: Vérifier si un nettoyage est nécessaire avant de l'effectuer
   */
  private async checkIfCleanupNeeded(nowSeconds: number): Promise<boolean> {
    if (!this.db) return false;

    try {
      // Vérifier rapidement les stores principaux
      const stores = [this.LOCATIONS_STORE, this.ADDRESSES_STORE];
      
      for (const storeName of stores) {
        const hasExpired = await this.hasExpiredItems(storeName, nowSeconds);
        if (hasExpired) {
          console.log(`🔍 Store ${storeName} a des données expirées`);
          return true;
        }
      }

      console.log(`📊 ${stores.length} stores vérifiés, ${0} ont besoin de nettoyage`);
      return false;
    } catch (error) {
      console.error('❌ Erreur vérification cleanup:', error);
      return true; // En cas d'erreur, on fait le nettoyage par sécurité
    }
  }

  /**
   * 🔍 NOUVEAU: Vérifier rapidement si un store a des éléments expirés
   */
  private async hasExpiredItems(storeName: string, nowSeconds: number): Promise<boolean> {
    let transaction: IDBTransaction;
    try {
      transaction = await this.getTransaction([storeName], 'readonly');
    } catch {
      // Cache indisponible (Safari a fermé la connexion) : on considère
      // qu'il n'y a rien à nettoyer plutôt que de faire planter la routine.
      return false;
    }

    return new Promise((resolve) => {
      const store = transaction.objectStore(storeName);
      const request = store.openCursor();
      
      let checkedCount = 0;
      const maxCheck = 10; // Vérifier seulement les 10 premiers éléments

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor || checkedCount >= maxCheck) {
          resolve(false); // Pas d'expirés trouvés dans l'échantillon
          return;
        }

        const item = cursor.value;
        let isExpired = false;

        if (storeName === this.LOCATIONS_STORE) {
          isExpired = (nowSeconds - item.ts) > item.ttl;
        } else if (storeName === this.ADDRESSES_STORE) {
          isExpired = (Date.now() - item.timestamp) > (item.ttl * 1000);
        }

        if (isExpired) {
          resolve(true); // Trouvé un élément expiré
          return;
        }

        checkedCount++;
        cursor.continue();
      };

      request.onerror = () => resolve(false);
    });
  }


  private async cleanupStore(storeName: string, isExpired: (item: any) => boolean): Promise<number> {
    const transaction = await this.getTransaction([storeName], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        const items = request.result;
        const expiredItems = items.filter(isExpired);

        console.log(`📊 ${storeName}: ${items.length} éléments, ${expiredItems.length} expirés`);

        expiredItems.forEach(item => {
          let key;
          
          // 🎯 DÉTERMINER LA CLÉ SELON LE STORE
          if (storeName === this.LOCATIONS_STORE) {
            key = item.id || 'current'; // Fallback pour locations
          } else if (storeName === this.ADDRESSES_STORE) {
            key = item.query || item.address; // Fallback pour addresses
          } else {
            // Pour les autres stores, utiliser l'ID de l'événement
            key = item.id || item.eventId || item.key;
          }
          
          // 🚨 SÉCURITÉ : Ne pas supprimer si pas de clé valide
          if (key && key !== null && key !== undefined) {
            store.delete(key);
          } else {
            console.warn(`⚠️ Clé invalide pour suppression dans ${storeName}:`, item);
          }
        });

        resolve(expiredItems.length);
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Méthodes utilitaires
  private async clearLocation(): Promise<void> {
    const transaction = await this.getTransaction([this.LOCATIONS_STORE], 'readwrite');
    const store = transaction.objectStore(this.LOCATIONS_STORE);
    store.delete('current');
  }

  private async clearAddress(query: string): Promise<void> {
    const transaction = await this.getTransaction([this.ADDRESSES_STORE], 'readwrite');
    const store = transaction.objectStore(this.ADDRESSES_STORE);
    store.delete(query.toLowerCase());
  }

  // 🗑️ DEPRECATED METHODS - Commentées car remplacées par Smart Cache
  
  /*
  // 🗑️ DEPRECATED - Utiliser saveGeoEvent() ou saveOrganizerEvent() selon le contexte
  async saveEvent(event: any, zoneKey?: string): Promise<void> {
    // 1. Sauvegarder l'événement
    await this.saveEventToDB(event, zoneKey);
    
    // 2. Vérifier les limites
    const eventCount = await this.getEventCount();
    if (eventCount > this.LIMITS.maxEvents) {
      console.log(`⚠️ Limite d'événements dépassée (${eventCount}/${this.LIMITS.maxEvents})`);
      await this.cleanupOldEvents();
    }
  }

  private async saveEventToDB(event: any, zoneKey?: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.EVENTS_STORE], 'readwrite');
      const store = transaction.objectStore(this.EVENTS_STORE);

      const eventData = {
        ...event,
        timestamp: Date.now(),
        zoneKey: zoneKey || null
      };

      const request = store.put(eventData);

      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        console.error('❌ Erreur lors de la sauvegarde de l\'événement:', request.error);
        reject(request.error);
      };
    });
  }

  private async cleanupOldEvents(): Promise<void> {
    // Supprimer les 20% les plus anciens
    const events = await this.getAllEvents();
    const toDelete = events
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(0, Math.ceil(events.length * 0.2));
    
    for (const event of toDelete) {
      await this.clearEvent(event.id);
    }
  }
  */

  /*
  // 🗑️ DEPRECATED - Remplacé par le Smart Cache avec saveGeoEvent() individuel + listeners
  async saveZoneEvents(zoneKey: string, radius: number, events: any[]): Promise<void> {
    console.log('💾 Sauvegarde des événements de zone:', zoneKey, `(${events.length} événements)`);
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.ZONES_STORE], 'readwrite');
      const store = transaction.objectStore(this.ZONES_STORE);

      const zoneData = {
        key: zoneKey,
        radius: radius, // ✅ Garder le rayon pour référence
        events: events, // ✅ Tous les événements de la zone
        timestamp: Date.now(),
        ttl: this.getZoneTTL(radius)
      };

      const request = store.put(zoneData);

      request.onsuccess = () => {
        console.log('✅ Événements de zone sauvegardés avec succès');
        resolve();
      };
      request.onerror = () => {
        console.error('❌ Erreur lors de la sauvegarde des événements de zone:', request.error);
        reject(request.error);
      };
    });
  }
  */

  /*
  // 🗑️ DEPRECATED - Remplacé par getGeoEvents() dans le Smart Cache
  async getZoneEvents(zoneKey: string, radius: number): Promise<any[] | null> {
    console.log('🔍 Recherche des événements de zone en cache:', zoneKey, `(rayon: ${radius}km)`);
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.ZONES_STORE], 'readonly');
      const store = transaction.objectStore(this.ZONES_STORE);
      const request = store.get(zoneKey);

      request.onsuccess = () => {
        const cached = request.result;
        if (!cached) {
          console.log('❌ Événements de zone non trouvés en cache');
          resolve(null);
          return;
        }

        const now = Date.now();
        
        // ✅ NOUVELLE LOGIQUE : Vérifier les TTL individuels des événements
        const validEvents = cached.events.filter((event: any) => {
          // Calculer le TTL individuel de chaque événement avec contexte intelligent
          const context = event.organizerId ? 'organizer' : 'public';
          const eventTTL = this.calculateEventTTL(event.endTime, context);
          return eventTTL > 0; // Événement encore valide
        });

        if (validEvents.length > 0) {
          console.log('✅ Événements de zone en cache valides');
          
          // Si certains événements ont expiré, mettre à jour le cache de zone
          if (validEvents.length < cached.events.length) {
            console.log(`🧹 ${cached.events.length - validEvents.length} événements expirés supprimés de la zone`);
            // this.saveZoneEvents(zoneKey, radius, validEvents); // DEPRECATED method call removed
          }
          
          // ✅ Filtrer selon le rayon demandé
          const filteredEvents = this.filterEventsByRadius(validEvents, radius);
          console.log(`📊 ${filteredEvents.length}/${validEvents.length} événements dans le rayon de ${radius}km`);
          
          resolve(filteredEvents);
        } else {
          console.log('⏰ Tous les événements de zone ont expiré, suppression...');
          this.clearZone(zoneKey);
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('❌ Erreur lors de la récupération des événements de zone:', request.error);
        reject(request.error);
      };
    });
  }
  */

  /**
   * Filtre les événements selon le rayon demandé
   */

  /*
  // 🗑️ DEPRECATED - Méthodes de nettoyage legacy
  private async clearEvent(eventId: string): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction([this.EVENTS_STORE], 'readwrite');
    const store = transaction.objectStore(this.EVENTS_STORE);
    store.delete(eventId);
  }

  private async clearZone(zoneKey: string): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction([this.ZONES_STORE], 'readwrite');
    const store = transaction.objectStore(this.ZONES_STORE);
    store.delete(zoneKey);
  }
  */

  // Nouvelles méthodes pour les séries d'événements
  async saveSeries(series: any): Promise<void> {
    // 🔇 LOG RETIRÉ: Plus de log individuel par série
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.SERIES_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.SERIES_STORE);

      const seriesData = {
        ...series,
        timestamp: Date.now(),
        ttl: 24 * 60 * 60 * 1000 // 24h pour les séries
      };

      const request = store.put(seriesData);

      request.onsuccess = () => {
        // 🔇 LOG RETIRÉ: Plus de log success par série
        resolve();
      };
      request.onerror = () => {
        console.error('❌ Erreur lors de la sauvegarde de la série:', request.error);
        reject(request.error);
      };
    });
  }

  async getSeries(seriesId: string): Promise<any | null> {
    console.log('🔍 Recherche de la série en cache:', seriesId);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.SERIES_STORE], 'readonly');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.SERIES_STORE);
      const request = store.get(seriesId);

      request.onsuccess = () => {
        const cached = request.result;
        if (!cached) {
          console.log('❌ Série non trouvée en cache');
          resolve(null);
          return;
        }

        // Vérifier si la série n'est pas expirée
        const now = Date.now();
        const age = now - cached.timestamp;
        
        if (age < cached.ttl) {
          console.log('✅ Série en cache valide');
          resolve(cached);
        } else {
          console.log('⏰ Série en cache expirée, suppression...');
          this.clearSeries(seriesId).catch(() => {});
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('❌ Erreur lors de la récupération de la série:', request.error);
        reject(request.error);
      };
    });
  }

  async saveSeriesEvents(seriesId: string, events: any[], seriesData?: { endDate?: string; endTime?: string }): Promise<void> {
    console.log('💾 Sauvegarde des événements de série:', seriesId);
    if (!this.db) await this.init();

    // 🔧 NOUVEAU : TTL intelligent basé sur la fin de série
    let ttl = 24 * 60 * 60 * 1000; // Fallback 24h
    
    if (seriesData?.endDate && seriesData?.endTime) {
      // Importer la fonction calculateSeriesTTL
      const { calculateSeriesTTL } = await import('../types/cache.types');
      ttl = calculateSeriesTTL(seriesData.endDate, seriesData.endTime);
      console.log(`🕐 TTL série calculé: ${Math.round(ttl/1000/60/60)}h pour fin ${seriesData.endDate} ${seriesData.endTime}`);
    }

    const transaction = await this.getTransaction([this.SERIES_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.SERIES_STORE);

      const seriesEventsData = {
        id: `series_events_${seriesId}`,
        seriesId,
        events,
        timestamp: Date.now(),
        ttl
      };

      const request = store.put(seriesEventsData);

      request.onsuccess = () => {
        console.log('✅ Événements de série sauvegardés avec succès');
        resolve();
      };
      request.onerror = () => {
        console.error('❌ Erreur lors de la sauvegarde des événements de série:', request.error);
        reject(request.error);
      };
    });
  }

  async getSeriesEvents(seriesId: string): Promise<any[] | null> {
    console.log('🔍 Recherche des événements de série en cache:', seriesId);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.SERIES_STORE], 'readonly');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.SERIES_STORE);
      const request = store.get(`series_events_${seriesId}`);

      request.onsuccess = () => {
        const cached = request.result;
        if (!cached) {
          console.log('❌ Événements de série non trouvés en cache');
          resolve(null);
          return;
        }

        // Vérifier si les événements ne sont pas expirés
        const now = Date.now();
        const age = now - cached.timestamp;
        
        if (age < cached.ttl) {
          console.log('✅ Événements de série en cache valides');
          resolve(cached.events);
        } else {
          console.log('⏰ Événements de série en cache expirés, suppression...');
          this.clearSeriesEvents(seriesId).catch(() => {});
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('❌ Erreur lors de la récupération des événements de série:', request.error);
        reject(request.error);
      };
    });
  }

  private async clearSeries(seriesId: string): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction([this.SERIES_STORE], 'readwrite');
    const store = transaction.objectStore(this.SERIES_STORE);
    store.delete(seriesId);
  }

  private async clearSeriesEvents(seriesId: string): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction([this.SERIES_STORE], 'readwrite');
    const store = transaction.objectStore(this.SERIES_STORE);
    store.delete(`series_events_${seriesId}`);
  }



  /**
   * 🚀 NOUVEAU : Compte le nombre d'événements favoris actifs de manière optimisée
   * Utilisé par BottomNav pour afficher le badge
   * ✅ CORRECTION : Compte dans FAVORITE_EVENTS_STORE (le bon store utilisé par appStore)
   */
  async getFavoritesCount(): Promise<number> {
    console.log('🔢 Comptage optimisé des événements favoris...');
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.FAVORITE_EVENTS_STORE], 'readonly');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.FAVORITE_EVENTS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        const favoriteEvents = request.result || [];
        const now = new Date();

        // ✅ Filtrer les événements valides (même logique que getFavoriteEventsFromDB)
        const validEvents = favoriteEvents.filter((event: any) => {
          try {
            // Validation robuste des dates (même logique que appStore.loadFavorites)
            let expiresAt: Date;

            if (event.endTime) {
              const endTime = new Date(event.endTime);
              if (isNaN(endTime.getTime())) {
                expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // Fallback 30 jours
              } else {
                expiresAt = endTime;
              }
            } else if (event.datetime) {
              const datetime = new Date(event.datetime);
              if (isNaN(datetime.getTime())) {
                expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // Fallback 30 jours
              } else {
                // Expirer 24h après le début
                expiresAt = new Date(datetime.getTime() + 24 * 60 * 60 * 1000);
              }
            } else {
              // Aucune date disponible : garder 30 jours
              expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
            }

            return expiresAt > now && event.isFavorite !== false;
          } catch (error) {
            // En cas d'erreur, garder l'événement
            return true;
          }
        });

        // 🆕 GROUPER PAR SERIES : compter 1 pour toute une série, pas chaque occurrence
        const seriesIds = new Set<string>();
        let simpleEventsCount = 0;

        validEvents.forEach((event: any) => {
          if (event.seriesId) {
            // Événement récurrent : ajouter au Set (dédupliqué automatiquement)
            seriesIds.add(event.seriesId);
          } else {
            // Événement simple : compter directement
            simpleEventsCount++;
          }
        });

        const totalCount = seriesIds.size + simpleEventsCount;

        console.log(`🎯 ${totalCount} événements favoris actifs comptés (${seriesIds.size} séries + ${simpleEventsCount} simples)`);
        resolve(totalCount);
      };

      request.onerror = () => {
        console.error('❌ Erreur lors du comptage des événements favoris:', request.error);
        reject(request.error);
      };
    });
  }









  async saveHiddenEvent(eventId: string, reason: 'swipe' | 'favorite', seriesId?: string): Promise<void> {
    if (!this.db) {
      console.warn('⚠️ IndexedDB non initialisé pour saveHiddenEvent');
      return;
    }

    const hiddenEvent: CachedHiddenEvent = {
      eventId,
      seriesId,
      reason,
      timestamp: Date.now()
    };

    try {
      const transaction = this.db.transaction([this.HIDDEN_EVENTS_STORE], 'readwrite');
      const store = transaction.objectStore(this.HIDDEN_EVENTS_STORE);
      const request = store.put(hiddenEvent);
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          console.log('💾 Événement masqué sauvegardé:', eventId, reason);
          // 🔔 Dispatch global pour synchroniser tous les composants
          window.dispatchEvent(new CustomEvent('hiddenEventsUpdated'));
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('❌ Erreur sauvegarde événement masqué:', error);
      throw error;
    }
  }

  async getHiddenEvent(eventId: string): Promise<CachedHiddenEvent | null> {
    if (!this.db) {
      console.warn('⚠️ IndexedDB non initialisé pour getHiddenEvent');
      return null;
    }

    try {
      const transaction = this.db.transaction([this.HIDDEN_EVENTS_STORE], 'readonly');
      const store = transaction.objectStore(this.HIDDEN_EVENTS_STORE);
      const request = store.get(eventId);
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('❌ Erreur récupération événement masqué:', error);
      return null;
    }
  }

  async getAllHiddenEvents(): Promise<CachedHiddenEvent[]> {
    if (!this.db) {
      console.warn('⚠️ IndexedDB non initialisé pour getAllHiddenEvents');
      return [];
    }

    try {
      const transaction = this.db.transaction([this.HIDDEN_EVENTS_STORE], 'readonly');
      const store = transaction.objectStore(this.HIDDEN_EVENTS_STORE);
      const request = store.getAll();
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('❌ Erreur récupération événements masqués:', error);
      return [];
    }
  }

  async removeHiddenEvent(eventId: string): Promise<void> {
    if (!this.db) {
      console.warn('⚠️ IndexedDB non initialisé pour removeHiddenEvent');
      return;
    }

    try {
      console.log('REMOVE HIDDEN EVENT', eventId);
      const transaction = this.db.transaction([this.HIDDEN_EVENTS_STORE], 'readwrite');
      const store = transaction.objectStore(this.HIDDEN_EVENTS_STORE);
      const request = store.delete(eventId);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          console.log('🗑️ Événement masqué supprimé:', eventId);
          window.dispatchEvent(new CustomEvent('hiddenEventsUpdated'));
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('❌ Erreur suppression événement masqué:', error);
      throw error;
    }
  }

  async cleanupHiddenEvents(): Promise<number> {
    if (!this.db) {
      console.warn('⚠️ IndexedDB non initialisé pour cleanupHiddenEvents');
      return 0;
    }

    try {
      const transaction = this.db.transaction([this.HIDDEN_EVENTS_STORE], 'readwrite');
      const store = transaction.objectStore(this.HIDDEN_EVENTS_STORE);
      const request = store.getAll();
      
      return new Promise((resolve, reject) => {
        request.onsuccess = async () => {
          try {
            const allEvents: CachedHiddenEvent[] = request.result || [];
            const now = Date.now();
            const toDelete: string[] = [];
            
            // Supprimer les événements masqués depuis plus de 30 jours
            const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
            
            allEvents.forEach((event: CachedHiddenEvent) => {
              if (event.timestamp < thirtyDaysAgo) {
                toDelete.push(event.eventId);
              }
            });
            
            // Supprimer en lot
            for (const eventId of toDelete) {
              await store.delete(eventId);
            }
            
            console.log(`✅ ${toDelete.length} événements masqués nettoyés`);
            resolve(toDelete.length);
          } catch (error) {
            reject(error);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('❌ Erreur nettoyage événements masqués:', error);
      return 0;
    }
  }

  // 🆕 SMART CACHE - NOUVELLES MÉTHODES POUR LES 3 CONTEXTES

  /**
   * 📍 CONTEXTE GÉO - Sauvegarde événement dans store géographique (TTL 7 jours)
   * 🌍 GEOHASH: Calcule et stocke le geohash pour indexation spatiale
   */
  async saveGeoEvent(event: any, zoneKey: string): Promise<void> {
    // 🔇 LOG RETIRÉ: Plus de log individuel par event
    if (!this.db) await this.init();

    // Validation des données critiques
    if (!event.id || !event.latitude || !event.longitude) {
      console.warn('❌ [GEO] Événement géo invalide - coordonnées manquantes:', {
        id: event.id,
        hasLatitude: !!event.latitude,
        hasLongitude: !!event.longitude
      });
      return;
    }

    // 🌍 GEOHASH: Calculer le geohash si absent
    let eventGeohash = event.geohash;
    if (!eventGeohash && event.latitude && event.longitude) {
      const geohashLib = await import('ngeohash');
      eventGeohash = geohashLib.default.encode(event.latitude, event.longitude, 5); // Precision 5 = ~4.9km
    }

    // ✅ OPTIMISATION ANTI-RERENDER - Éviter recréation timestamps identiques
    // ✅ NOUVEAU : TTL basé sur endTime ou datetime + 2h
    let expiresAt: number;
    let timestamp = Date.now();

    if (event.endTime) {
      // TTL = endTime de l'événement
      const endTime = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
      if (!isNaN(endTime.getTime())) {
        expiresAt = endTime.getTime();
      } else {
        console.warn('⚠️ [GEO] endTime invalide pour', event.id + ':', event.endTime);
        // Fallback : datetime + 2h ou 7 jours
        if (event.datetime) {
          const eventDate = event.datetime instanceof Date ? event.datetime : new Date(event.datetime);
          expiresAt = eventDate.getTime() + (2 * 60 * 60 * 1000); // +2h
        } else {
          expiresAt = Date.now() + (7 * 24 * 60 * 60 * 1000); // +7 jours
        }
      }
    } else if (event.datetime) {
      // Pas d'endTime : TTL = datetime + 2h
      const eventDate = event.datetime instanceof Date ? event.datetime : new Date(event.datetime);
      if (!isNaN(eventDate.getTime())) {
        expiresAt = eventDate.getTime() + (2 * 60 * 60 * 1000); // +2h
      } else {
        console.warn('⚠️ [GEO] datetime invalide pour', event.id + ':', event.datetime);
        expiresAt = Date.now() + (7 * 24 * 60 * 60 * 1000); // Fallback 7 jours
      }
    } else {
      // Ni endTime ni datetime : fallback 7 jours
      console.warn('⚠️ [GEO] Aucune date pour', event.id);
      expiresAt = Date.now() + (7 * 24 * 60 * 60 * 1000);
    }
    
    // Vérifier si l'événement existe déjà pour réutiliser ses timestamps
    const existingEvent = await this.getExistingGeoEvent(event.id, zoneKey);
    if (existingEvent && existingEvent.expiresAt && existingEvent.timestamp) {
      // Réutiliser les timestamps existants si TTL similaire (±1 jour)
      const existingTTL = existingEvent.expiresAt - existingEvent.timestamp;
      const expectedTTL = expiresAt - timestamp;
      
      if (Math.abs(existingTTL - expectedTTL) < 24 * 60 * 60 * 1000) {
        expiresAt = existingEvent.expiresAt;
        timestamp = existingEvent.timestamp;
      }
    }

    // ✅ APPLIQUER NORMALISATION ANTI-RERENDER à l'événement avant sauvegarde
    const { normalizeSupabaseEvent } = await import('../supabase/SmartListenerService');
    const normalizedEvent = normalizeSupabaseEvent(event);

    const transaction = await this.getTransaction([this.GEO_EVENTS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.GEO_EVENTS_STORE);

      const request = store.put({
        ...normalizedEvent,
        geohash: eventGeohash, // 🌍 GEOHASH: Stocker pour indexation spatiale
        zoneKey, // Garder pour compatibilité arrière
        context: 'geo',
        expiresAt,
        timestamp
      });

      request.onsuccess = () => {
        // 🔇 LOG RETIRÉ: Plus de log success par event
        resolve();
      };
      request.onerror = () => {
        console.error('❌ [GEO] Erreur sauvegarde événement géo:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 🔍 HELPER - Récupérer un événement géo existant pour réutiliser ses timestamps
   */
  private async getExistingGeoEvent(eventId: string, zoneKey: string): Promise<any | null> {
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.GEO_EVENTS_STORE], 'readonly');

    return new Promise((resolve) => {
      const store = transaction.objectStore(this.GEO_EVENTS_STORE);
      const index = store.index('zoneKey');
      const request = index.getAll(zoneKey);

      request.onsuccess = () => {
        const events = request.result || [];
        const existing = events.find(event => event.id === eventId);
        resolve(existing || null);
      };

      request.onerror = () => {
        // En cas d'erreur, continuer sans optimisation
        resolve(null);
      };
    });
  }

  /**
   * 📍 CONTEXTE GÉO - Récupération événements zone géographique
   */
  async getGeoEvents(zoneKey: string, radius: number): Promise<any[]> {
    console.log('🔍 [GEO] Récupération événements géo:', zoneKey, `rayon ${radius}km`);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.GEO_EVENTS_STORE], 'readonly');

    return new Promise(async (resolve, reject) => {
      const store = transaction.objectStore(this.GEO_EVENTS_STORE);
      const index = store.index('zoneKey');
      const request = index.getAll(zoneKey);

      request.onsuccess = async () => {
        const events = request.result || [];
        const now = Date.now();
        
        // Filtrer événements non expirés
        const validEvents = events.filter(event => event.expiresAt > now);
        
        // ✅ CORRECTION ANTI-RERENDER : Normaliser tous les événements récupérés
        const { normalizeSupabaseEvent } = await import('../supabase/SmartListenerService');
        const normalizedEvents = validEvents.map(event => {
          // Si l'événement est déjà normalisé (frozen), le retourner tel quel
          if (Object.isFrozen(event)) {
            return event;
          }
          // Sinon, le normaliser avec le cache intelligent
          return normalizeSupabaseEvent(event);
        });
        
        console.log(`✅ [GEO] ${normalizedEvents.length}/${events.length} événements géo valides trouvés`);
        resolve(normalizedEvents);
      };

      request.onerror = () => {
        console.error('❌ [GEO] Erreur récupération événements géo:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 🌍 CONTEXTE GÉO - Récupération événements par liste de geohash (fusion automatique)
   * Cette méthode permet de récupérer les events de plusieurs zones geohash adjacentes
   * et de les fusionner automatiquement (dédupliqués par ID).
   *
   * @param geohashes - Liste des geohash (ex: centre + 8 voisins = 9 zones)
   * @returns Events dédupliqués et normalisés de toutes les zones
   */
  async getGeoEventsByGeohashes(geohashes: string[]): Promise<any[]> {
    console.log('🌍 [GEO-Geohash] Récupération events pour', geohashes.length, 'geohashes');
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.GEO_EVENTS_STORE], 'readonly');

    return new Promise(async (resolve, reject) => {
      const store = transaction.objectStore(this.GEO_EVENTS_STORE);
      const request = store.getAll();

      request.onsuccess = async () => {
        const allEvents = request.result || [];
        const now = Date.now();

        // Filtrer par:
        // 1. Non expiré (expiresAt > now)
        // 2. Geohash commence par un des préfixes demandés
        const validEvents = allEvents.filter(event => {
          // Vérifier expiration
          if (event.expiresAt && event.expiresAt <= now) return false;

          // Vérifier geohash
          if (!event.geohash) return false;

          // Vérifier si le geohash de l'event match un des préfixes
          return geohashes.some(prefix => event.geohash.startsWith(prefix));
        });

        // Dédupliquer par ID (un event peut être dans plusieurs zones)
        const uniqueEvents = Array.from(
          new Map(validEvents.map(e => [e.id, e])).values()
        );

        // Normaliser avec le cache intelligent anti-rerender
        const { normalizeSupabaseEvent } = await import('../supabase/SmartListenerService');
        const normalizedEvents = uniqueEvents.map(event => {
          // Si l'événement est déjà normalisé (frozen), le retourner tel quel
          if (Object.isFrozen(event)) {
            return event;
          }
          // Sinon, le normaliser avec le cache intelligent
          return normalizeSupabaseEvent(event);
        });

        console.log(`✅ [GEO-Geohash] ${normalizedEvents.length} events uniques trouvés pour ${geohashes.length} geohashes`);
        resolve(normalizedEvents);
      };

      request.onerror = () => {
        console.error('❌ [GEO-Geohash] Erreur récupération événements:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 🧹 CONTEXTE GÉO - Suppression des events périmés d'une zone
   * Quand le SmartListener charge des events frais depuis Supabase,
   * les events en cache IndexedDB qui ne sont plus dans les données fraîches
   * doivent être supprimés (events supprimés de la BDD, séries recréées, etc.)
   *
   * @param freshEventIds - IDs des events frais venant de Supabase
   * @param geohashes - Geohashes de la zone concernée
   * @returns Nombre d'events supprimés
   */
  async removeStaleGeoEvents(freshEventIds: Set<string>, geohashes: string[]): Promise<number> {
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.GEO_EVENTS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.GEO_EVENTS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        const allEvents = request.result || [];
        let removedCount = 0;

        // Trouver les events en cache qui matchent la zone mais ne sont plus dans les données fraîches
        for (const event of allEvents) {
          if (!event.geohash) continue;

          // Vérifier si l'event est dans la zone concernée
          const isInZone = geohashes.some(prefix => event.geohash.startsWith(prefix));
          if (!isInZone) continue;

          // Si l'event n'est plus dans les données fraîches → supprimer
          if (!freshEventIds.has(event.id)) {
            store.delete(event.id);
            removedCount++;
          }
        }

        transaction.oncomplete = () => {
          if (removedCount > 0) {
            console.log(`🧹 [GEO] ${removedCount} events périmés supprimés du cache`);
          }
          resolve(removedCount);
        };

        transaction.onerror = () => {
          console.error('❌ [GEO] Erreur suppression events périmés:', transaction.error);
          reject(transaction.error);
        };
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * 🌍 CONTEXTE GÉO - Récupération événements par seriesId depuis geoEvents
   * Permet de récupérer TOUTES les occurrences d'une série récurrente depuis IndexedDB
   *
   * @param seriesId - ID de la série récurrente
   * @returns Tableau des événements de la série (normalisés et non expirés)
   */
  async getGeoEventsBySeriesId(seriesId: string): Promise<any[]> {
    console.log('🔍 [GEO-Series] Récupération événements de série depuis geoEvents:', seriesId);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.GEO_EVENTS_STORE], 'readonly');

    return new Promise(async (resolve, reject) => {
      const store = transaction.objectStore(this.GEO_EVENTS_STORE);
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = async () => {
        const allEvents = getAllRequest.result || [];
        const now = Date.now();

        // Filtrer par seriesId et événements non expirés
        const seriesEvents = allEvents.filter(event =>
          event.seriesId === seriesId && event.expiresAt > now
        );

        console.log(`🔍 [GEO-Series] Trouvé ${seriesEvents.length} événements de la série`);

        if (seriesEvents.length === 0) {
          resolve([]);
          return;
        }

        // Normaliser avec le cache intelligent anti-rerender
        const { normalizeSupabaseEvent } = await import('../supabase/SmartListenerService');
        const normalizedEvents = seriesEvents.map(event => {
          // Si l'événement est déjà normalisé (frozen), le retourner tel quel
          if (Object.isFrozen(event)) {
            return event;
          }
          // Sinon, le normaliser avec le cache intelligent
          return normalizeSupabaseEvent(event);
        });

        console.log(`✅ [GEO-Series] ${normalizedEvents.length} événements normalisés pour série ${seriesId.slice(0, 8)}...`);
        resolve(normalizedEvents);
      };

      getAllRequest.onerror = () => {
        console.error('❌ [GEO-Series] Erreur récupération événements série:', getAllRequest.error);
        reject(getAllRequest.error);
      };
    });
  }

  /**
   * 🏢 CONTEXTE ORGANISATEUR - Sauvegarde événement organisateur (TTL 6 mois)
   */
  // Compteur pour regrouper les warnings endTime
  private missingEndTimeEvents: string[] = [];
  private endTimeWarningTimeout: NodeJS.Timeout | null = null;

  private logMissingEndTimeWarning(eventId: string) {
    this.missingEndTimeEvents.push(eventId);
    
    // Débounce: attendre 100ms pour regrouper plusieurs events
    if (this.endTimeWarningTimeout) {
      clearTimeout(this.endTimeWarningTimeout);
    }
    
    this.endTimeWarningTimeout = setTimeout(() => {
      if (this.missingEndTimeEvents.length > 0) {
        console.warn(`⚠️ [ORG] ${this.missingEndTimeEvents.length} événements sans endTime, TTL fallback 6 mois:`, 
          this.missingEndTimeEvents.slice(0, 3).join(', ') + 
          (this.missingEndTimeEvents.length > 3 ? ` (+${this.missingEndTimeEvents.length - 3} autres)` : '')
        );
        this.missingEndTimeEvents = [];
      }
    }, 100);
  }

  async saveOrganizerEvent(event: any): Promise<void> {
    // 🔇 LOG RETIRÉ: Plus de log individuel par event
    if (!this.db) await this.init();

    // Validation des données critiques
    if (!event.id || !event.organizerId) {
      console.warn('❌ [ORG] Événement organisateur invalide:', {
        id: event.id,
        hasOrganizerId: !!event.organizerId
      });
      return;
    }

    // TTL pour organisateur: 6 mois
    const ttl = 180 * 24 * 60 * 60 * 1000; // 6 mois en millisecondes
    let expiresAt = Date.now() + ttl;

    // TTL intelligent basé sur endTime si disponible
    if (event.endTime) {
      const endTime = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
      if (!isNaN(endTime.getTime())) {
        expiresAt = Math.max(endTime.getTime() + (30 * 24 * 60 * 60 * 1000), Date.now() + ttl);
        // 🔇 LOG RETIRÉ: Plus de log TTL par event
      } else {
        // Log groupé pour endTime invalide
        this.logMissingEndTimeWarning(event.id);
      }
    } else {
      // Log groupé pour endTime manquant
      this.logMissingEndTimeWarning(event.id);
    }

    const transaction = await this.getTransaction([this.ORGANIZER_EVENTS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.ORGANIZER_EVENTS_STORE);

      const request = store.put({
        ...event,
        context: 'organizer',
        expiresAt,
        timestamp: Date.now()
      });

      request.onsuccess = () => {
        // 🔇 LOG RETIRÉ: Plus de log success par event
        resolve();
      };
      request.onerror = () => {
        console.error('❌ [ORG] Erreur sauvegarde événement organisateur:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 🏢 CONTEXTE ORGANISATEUR - Récupération événements organisateur (LEGACY avec filtrage)
   * @deprecated Utiliser getAllOrganizerEventsFromDB() + tri côté client pour un seul listener
   */
  async getOrganizerEventsFromDB(organizerId: string, category: 'active' | 'draft' | 'past'): Promise<any[]> {
    console.log('🔍 [ORG] Récupération événements organisateur (LEGACY):', organizerId, category);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.ORGANIZER_EVENTS_STORE], 'readonly');

    return new Promise(async (resolve, reject) => {
      const store = transaction.objectStore(this.ORGANIZER_EVENTS_STORE);
      const index = store.index('organizerId');
      const request = index.getAll(organizerId);

      request.onsuccess = async () => {
        const allEvents = request.result || [];
        const now = new Date();

        // Filtrer par catégorie et validité
        const filteredEvents = allEvents.filter(event => {
          // Vérifier expiration
          if (event.expiresAt <= Date.now()) return false;
          
          // Filtrer par catégorie
          if (category === 'active') {
            return event.status === 'active' && event.datetime && new Date(event.datetime) >= now;
          } else if (category === 'draft') {
            return event.status === 'draft';
          } else { // past
            return event.status === 'active' && event.datetime && new Date(event.datetime) < now;
          }
        });

        // ✅ CORRECTION ANTI-RERENDER : Normaliser tous les événements récupérés
        const { normalizeSupabaseEvent } = await import('../supabase/SmartListenerService');
        const normalizedEvents = filteredEvents.map(event => {
          // Si l'événement est déjà normalisé (frozen), le retourner tel quel
          if (Object.isFrozen(event)) {
            return event;
          }
          // Sinon, le normaliser avec le cache intelligent
          return normalizeSupabaseEvent(event);
        });

        console.log(`✅ [ORG] ${normalizedEvents.length}/${allEvents.length} événements organisateur ${category} trouvés`);
        resolve(normalizedEvents);
      };

      request.onerror = () => {
        console.error('❌ [ORG] Erreur récupération événements organisateur:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 🏢 CONTEXTE ORGANISATEUR - Récupération TOUS les événements organisateur
   * Nouvelle méthode optimisée pour un seul listener avec tri côté client
   */
  async getAllOrganizerEventsFromDB(organizerId: string): Promise<any[]> {
    console.log('🔍 [ORG] Récupération TOUS les événements organisateur:', organizerId);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.ORGANIZER_EVENTS_STORE], 'readonly');

    return new Promise(async (resolve, reject) => {
      const store = transaction.objectStore(this.ORGANIZER_EVENTS_STORE);
      const index = store.index('organizerId');
      const request = index.getAll(organizerId);

      request.onsuccess = async () => {
        const allEvents = request.result || [];

        // Filtrer seulement les événements non expirés
        const validEvents = allEvents.filter(event => {
          return event.expiresAt > Date.now();
        });

        // ✅ CORRECTION ANTI-RERENDER : Normaliser tous les événements récupérés
        const { normalizeSupabaseEvent } = await import('../supabase/SmartListenerService');
        const normalizedEvents = validEvents.map(event => {
          // Si l'événement est déjà normalisé (frozen), le retourner tel quel
          if (Object.isFrozen(event)) {
            return event;
          }
          // Sinon, le normaliser avec le cache intelligent
          return normalizeSupabaseEvent(event);
        });

        console.log(`✅ [ORG] ${normalizedEvents.length}/${allEvents.length} événements organisateur valides trouvés`);
        resolve(normalizedEvents);
      };

      request.onerror = () => {
        console.error('❌ [ORG] Erreur récupération TOUS les événements organisateur:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 🚀 NOUVEAU : Compte les événements organisateur par catégorie de manière optimisée
   * Utilisé par useSmartOrganizerEvents pour afficher les compteurs d'onglets
   */
  async getOrganizerEventsCounts(organizerId: string): Promise<{ active: number; draft: number; past: number }> {
    console.log('🔢 [ORG] Comptage optimisé des événements organisateur:', organizerId);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.ORGANIZER_EVENTS_STORE], 'readonly');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.ORGANIZER_EVENTS_STORE);
      const index = store.index('organizerId');
      const request = index.getAll(organizerId);

      request.onsuccess = () => {
        const allEvents = request.result || [];
        const now = new Date();
        const counts = { active: 0, draft: 0, past: 0 };
        
        // ✅ NOUVEAU : Grouper par seriesId pour compter les événements récurrents comme 1 seul
        const seriesGroups = new Map<string, any[]>();
        const singleEvents: any[] = [];
        
        // Séparer les événements récurrents et simples
        allEvents.forEach(event => {
          // Vérifier expiration
          if (event.expiresAt <= Date.now()) return;
          
          if (event.seriesId) {
            // Événement récurrent : grouper par seriesId
            if (!seriesGroups.has(event.seriesId)) {
              seriesGroups.set(event.seriesId, []);
            }
            seriesGroups.get(event.seriesId)!.push(event);
          } else {
            // Événement simple
            singleEvents.push(event);
          }
        });
        
        // Compter les événements simples
        singleEvents.forEach(event => {
          if (event.status === 'draft') {
            counts.draft++;
          } else if (event.status === 'active') {
            if (event.datetime) {
              const eventDate = new Date(event.datetime);
              if (eventDate >= now) {
                counts.active++;
              } else {
                counts.past++;
              }
            } else {
              counts.active++;
            }
          }
        });
        
        // Compter les séries récurrentes (1 série peut être dans plusieurs catégories)
        seriesGroups.forEach((seriesEvents) => {
          // Prendre le premier événement de la série pour déterminer le statut de base
          const representativeEvent = seriesEvents[0];
          
          if (representativeEvent.status === 'draft') {
            counts.draft++;
          } else if (representativeEvent.status === 'active') {
            // ✅ NOUVEAU : Une série peut être à la fois active ET passée
            const futureOccurrences = seriesEvents.filter(event => {
              return event.datetime && new Date(event.datetime) >= now;
            });
            
            const pastOccurrences = seriesEvents.filter(event => {
              return event.datetime && new Date(event.datetime) < now;
            });
            
            // Compter dans actifs si elle a des occurrences futures
            if (futureOccurrences.length > 0) {
              counts.active++;
            }
            
            // Compter AUSSI dans passés si elle a des occurrences passées
            if (pastOccurrences.length > 0) {
              counts.past++;
            }
          }
        });

        console.log(`🎯 [ORG] Compteurs calculés pour ${organizerId}:`, counts, `(${singleEvents.length} simples + ${seriesGroups.size} séries = ${singleEvents.length + seriesGroups.size} total)`);
        resolve(counts);
      };

      request.onerror = () => {
        console.error('❌ [ORG] Erreur lors du comptage des événements organisateur:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * ❤️ CONTEXTE FAVORIS - Sauvegarde événement favori (TTL permanent)
   * Simplifié : stockage par eventId uniquement, comme les hiddenEvents
   */
  async saveFavoriteEvent(event: any): Promise<void> {
    console.log('💾 [FAV] Sauvegarde événement favori:', event.id);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.FAVORITE_EVENTS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.FAVORITE_EVENTS_STORE);

      const request = store.put({
        ...event,
        context: 'favorites',
        isFavorite: true,
        timestamp: Date.now()
      });

      request.onsuccess = () => {
        console.log('✅ [FAV] Événement favori sauvegardé:', event.id);
        resolve();
      };
      request.onerror = () => {
        console.error('❌ [FAV] Erreur sauvegarde événement favori:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * ❤️ CONTEXTE FAVORIS - Suppression événement favori
   * Simplifié : suppression par eventId uniquement, comme les hiddenEvents
   */
  async removeFavoriteEvent(eventId: string): Promise<void> {
    console.log('🗑️ [FAV] Suppression événement favori:', eventId);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.FAVORITE_EVENTS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.FAVORITE_EVENTS_STORE);
      const request = store.delete(eventId);

      request.onsuccess = () => {
        console.log('✅ [FAV] Événement favori supprimé:', eventId);
        resolve();
      };
      request.onerror = () => {
        console.error('❌ [FAV] Erreur suppression événement favori:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * ❤️ CONTEXTE FAVORIS - Sauvegarde série complète en favoris
   * Permet de stocker TOUTES les occurrences d'une série récurrente
   */
  async saveSeriesToFavorites(events: any[]): Promise<void> {
    console.log('💾 [FAV] Sauvegarde série complète en favoris:', events.length, 'événements');
    if (!this.db) await this.init();
    if (!events || events.length === 0) {
      console.warn('⚠️ [FAV] Aucun événement à sauvegarder');
      return;
    }

    const transaction = await this.getTransaction([this.FAVORITE_EVENTS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.FAVORITE_EVENTS_STORE);

      // Sauvegarder tous les événements de la série
      const promises = events.map(event => {
        return new Promise<void>((res, rej) => {
          const request = store.put({
            ...event,
            context: 'favorites',
            isFavorite: true,
            timestamp: Date.now()
          });
          request.onsuccess = () => res();
          request.onerror = () => rej(request.error);
        });
      });

      Promise.all(promises)
        .then(() => {
          console.log('✅ [FAV] Série complète sauvegardée:', events.length, 'événements');
          resolve();
        })
        .catch(error => {
          console.error('❌ [FAV] Erreur sauvegarde série complète:', error);
          reject(error);
        });
    });
  }

  /**
   * ❤️ CONTEXTE FAVORIS - Suppression série complète des favoris
   * Supprime TOUTES les occurrences d'une série par seriesId
   */
  async removeSeriesFromFavorites(seriesId: string): Promise<void> {
    console.log('🗑️ [FAV] Suppression série complète des favoris:', seriesId);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.FAVORITE_EVENTS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.FAVORITE_EVENTS_STORE);

      // 1. Récupérer tous les événements de la série
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const allEvents = getAllRequest.result || [];
        const seriesEvents = allEvents.filter(event => event.seriesId === seriesId);

        console.log(`🔍 [FAV] Trouvé ${seriesEvents.length} événements de la série à supprimer`);

        if (seriesEvents.length === 0) {
          console.log('✅ [FAV] Aucun événement de série à supprimer');
          resolve();
          return;
        }

        // 2. Supprimer tous les événements de la série
        const deletePromises = seriesEvents.map(event => {
          return new Promise<void>((res, rej) => {
            const deleteRequest = store.delete(event.id);
            deleteRequest.onsuccess = () => res();
            deleteRequest.onerror = () => rej(deleteRequest.error);
          });
        });

        Promise.all(deletePromises)
          .then(() => {
            console.log('✅ [FAV] Série complète supprimée:', seriesEvents.length, 'événements');
            resolve();
          })
          .catch(error => {
            console.error('❌ [FAV] Erreur suppression série complète:', error);
            reject(error);
          });
      };

      getAllRequest.onerror = () => {
        console.error('❌ [FAV] Erreur récupération événements série:', getAllRequest.error);
        reject(getAllRequest.error);
      };
    });
  }

  /**
   * ❤️ CONTEXTE FAVORIS - Récupération événements favoris
   * Simplifié : récupération de tous les favoris, comme les hiddenEvents
   */
  async getFavoriteEventsFromDB(): Promise<any[]> {
    console.log('🔍 [FAV] Récupération événements favoris');
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.FAVORITE_EVENTS_STORE], 'readonly');

    return new Promise(async (resolve, reject) => {
      const store = transaction.objectStore(this.FAVORITE_EVENTS_STORE);
      const request = store.getAll();

      request.onsuccess = async () => {
        const events = request.result || [];
        const now = new Date();

        // Filtrer événements favoris valides (pas expirés selon endTime)
        const validEvents = events.filter(event => {
          if (!event.isFavorite) return false;
          if (event.endTime && new Date(event.endTime) < now) return false;
          return true;
        });

        // ✅ CORRECTION ANTI-RERENDER : Normaliser tous les événements récupérés
        const { normalizeSupabaseEvent } = await import('../supabase/SmartListenerService');
        const normalizedEvents = validEvents.map(event => {
          // Si l'événement est déjà normalisé (frozen), le retourner tel quel
          if (Object.isFrozen(event)) {
            return event;
          }
          // Sinon, le normaliser avec le cache intelligent
          return normalizeSupabaseEvent(event);
        });

        console.log(`✅ [FAV] ${normalizedEvents.length}/${events.length} événements favoris valides trouvés`);
        resolve(normalizedEvents);
      };

      request.onerror = () => {
        console.error('❌ [FAV] Erreur récupération événements favoris:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 🧹 Nettoyage par contexte
   */
  async cleanupByContext(context: 'geo' | 'organizer' | 'favorites'): Promise<number> {
    const storeMap = {
      geo: this.GEO_EVENTS_STORE,
      organizer: this.ORGANIZER_EVENTS_STORE,
      favorites: this.FAVORITE_EVENTS_STORE
    };

    const storeName = storeMap[context];
    console.log(`🧹 [${context.toUpperCase()}] Nettoyage contexte ${context}`);
    
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([storeName], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        const events = request.result || [];
        const now = Date.now();
        let deletedCount = 0;

        events.forEach(event => {
          let shouldDelete = false;

          if (context === 'geo' || context === 'organizer') {
            // Vérifier expiration TTL
            shouldDelete = event.expiresAt && event.expiresAt <= now;
          } else if (context === 'favorites') {
            // Vérifier expiration événement
            shouldDelete = event.endTime && new Date(event.endTime) <= new Date();
          }

          if (shouldDelete) {
            store.delete(event.id);
            deletedCount++;
          }
        });

        console.log(`✅ [${context.toUpperCase()}] ${deletedCount} événements nettoyés`);
        resolve(deletedCount);
      };

      request.onerror = () => {
        console.error(`❌ [${context.toUpperCase()}] Erreur nettoyage:`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 📊 Statistiques par contexte
   */
  async getContextStats(): Promise<{ geo: number; organizer: number; favorites: number; series: number }> {
    if (!this.db) await this.init();

    const getCount = async (storeName: string): Promise<number> => {
      const transaction = await this.getTransaction([storeName], 'readonly');

      return new Promise((resolve, reject) => {
        const store = transaction.objectStore(storeName);
        const request = store.count();

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    };

    try {
      const [geo, organizer, favorites, series] = await Promise.all([
        getCount(this.GEO_EVENTS_STORE),
        getCount(this.ORGANIZER_EVENTS_STORE),
        getCount(this.FAVORITE_EVENTS_STORE),
        getCount(this.SERIES_STORE)
      ]);

      console.log(`📊 Stats contextes - Geo: ${geo}, Org: ${organizer}, Fav: ${favorites}, Series: ${series}`);
      return { geo, organizer, favorites, series };
    } catch (error) {
      console.error('❌ Erreur récupération stats contextes:', error);
      return { geo: 0, organizer: 0, favorites: 0, series: 0 };
    }
  }

  /**
   * 🔍 RECHERCHE GLOBALE - Trouve un événement par ID dans tous les stores SmartCache
   * Optimisé pour la récupération des favoris avec données complètes
   */
  // ✅ SUPPRIMÉ : findEventById n'est plus nécessaire avec le state management centralisé

  // 🆕 MÉTHODES PUBLIC ORGANIZERS - Nouveau store pour les profils publics

  /**
   * 📥 Récupère plusieurs organisateurs publics depuis IndexedDB
   * Optimisé pour le batch loading
   */
  async getPublicOrganizers(organizerIds: string[]): Promise<Map<string, any>> {
    console.log('🔍 [PublicOrg] Recherche de', organizerIds.length, 'organisateurs publics en cache');
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.PUBLIC_ORGANIZERS_STORE], 'readonly');

    return new Promise((resolve) => {
      const store = transaction.objectStore(this.PUBLIC_ORGANIZERS_STORE);
      const results = new Map<string, any>();
      let completed = 0;
      const now = Date.now();

      organizerIds.forEach(organizerId => {
        const request = store.get(organizerId);
        
        request.onsuccess = () => {
          const cached = request.result;
          if (cached && cached.expiresAt > now) {
            results.set(organizerId, cached);
          }
          
          completed++;
          if (completed === organizerIds.length) {
            console.log(`✅ [PublicOrg] ${results.size}/${organizerIds.length} organisateurs trouvés en cache`);
            resolve(results);
          }
        };

        request.onerror = () => {
          console.error('❌ [PublicOrg] Erreur lors de la récupération de l\'organisateur:', request.error);
          completed++;
          if (completed === organizerIds.length) {
            resolve(results);
          }
        };
      });
    });
  }

  /**
   * 💾 Sauvegarde plusieurs organisateurs publics en lot
   * TTL de 14 jours pour les profils publics (touristes)
   */
  async savePublicOrganizers(organizers: any[]): Promise<void> {
    console.log('💾 [PublicOrg] Sauvegarde en lot de', organizers.length, 'organisateurs publics');
    if (!this.db) await this.init();

    // TTL de 14 jours pour les organisateurs publics (plus long car moins changeants)
    const ttl = 14 * 24 * 60 * 60 * 1000; // 14 jours
    const expiresAt = Date.now() + ttl;

    const transaction = await this.getTransaction([this.PUBLIC_ORGANIZERS_STORE], 'readwrite');

    return new Promise((resolve) => {
      const store = transaction.objectStore(this.PUBLIC_ORGANIZERS_STORE);
      let completed = 0;

      organizers.forEach(organizer => {
        const request = store.put({
          ...organizer,
          expiresAt,
          timestamp: Date.now()
        });

        request.onsuccess = () => {
          completed++;
          if (completed === organizers.length) {
            console.log('✅ [PublicOrg] Tous les organisateurs publics sauvegardés avec succès');
            resolve();
          }
        };

        request.onerror = () => {
          console.error('❌ [PublicOrg] Erreur lors de la sauvegarde de l\'organisateur:', request.error);
          completed++;
          if (completed === organizers.length) {
            resolve();
          }
        };
      });
    });
  }

  /**
   * 🧹 Nettoie les organisateurs publics expirés
   */
  async cleanupPublicOrganizers(): Promise<number> {
    console.log('🧹 [PublicOrg] Nettoyage des organisateurs publics expirés...');
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.PUBLIC_ORGANIZERS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.PUBLIC_ORGANIZERS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        const organizers = request.result || [];
        const now = Date.now();
        let deletedCount = 0;

        organizers.forEach(org => {
          if (org.expiresAt <= now) {
            store.delete(org.id);
            deletedCount++;
          }
        });

        console.log(`✅ [PublicOrg] ${deletedCount} organisateurs publics expirés supprimés`);
        resolve(deletedCount);
      };

      request.onerror = () => {
        console.error('❌ [PublicOrg] Erreur lors du nettoyage des organisateurs publics:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 📊 Compte le nombre d'organisateurs publics en cache
   */
  async getPublicOrganizersCount(): Promise<number> {
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.PUBLIC_ORGANIZERS_STORE], 'readonly');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.PUBLIC_ORGANIZERS_STORE);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 🗑️ Supprime un organisateur public spécifique du cache
   * Utile pour forcer le rechargement d'un profil invalide/incomplet
   */
  async deletePublicOrganizer(organizerId: string): Promise<boolean> {
    console.log('🗑️ [PublicOrg] Suppression de l\'organisateur:', organizerId);
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.PUBLIC_ORGANIZERS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.PUBLIC_ORGANIZERS_STORE);
      const request = store.delete(organizerId);

      request.onsuccess = () => {
        console.log('✅ [PublicOrg] Organisateur supprimé du cache:', organizerId);
        resolve(true);
      };

      request.onerror = () => {
        console.error('❌ [PublicOrg] Erreur lors de la suppression:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * 🧹 Nettoie les organisateurs publics invalides (sans champ 'organizer')
   * Corrige les données obsolètes de la migration Firebase → Supabase
   */
  async cleanupInvalidPublicOrganizers(): Promise<number> {
    console.log('🧹 [PublicOrg] Nettoyage des organisateurs publics invalides...');
    if (!this.db) await this.init();

    const transaction = await this.getTransaction([this.PUBLIC_ORGANIZERS_STORE], 'readwrite');

    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(this.PUBLIC_ORGANIZERS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        const organizers = request.result || [];
        let deletedCount = 0;

        organizers.forEach(org => {
          // Supprimer les profils sans champ 'organizer' (nom d'affichage)
          if (!org.organizer) {
            console.log('🗑️ [PublicOrg] Suppression profil invalide (sans nom):', org.id);
            store.delete(org.id);
            deletedCount++;
          }
        });

        console.log(`✅ [PublicOrg] ${deletedCount} organisateurs publics invalides supprimés`);
        resolve(deletedCount);
      };

      request.onerror = () => {
        console.error('❌ [PublicOrg] Erreur lors du nettoyage des organisateurs invalides:', request.error);
        reject(request.error);
      };
    });
  }

  // ============================================
  // 🧹 AUTOMATIC CLEANUP - PRD-10
  // ============================================

  /**
   * 🧹 Nettoie les événements expirés de tous les stores
   * Supprime les événements dont endTime < now pour libérer de l'espace
   */
  async cleanupExpiredEvents(): Promise<{ geoEvents: number; organizerEvents: number; favoriteEvents: number }> {
    const now = new Date().toISOString();
    const results = {
      geoEvents: 0,
      organizerEvents: 0,
      favoriteEvents: 0
    };

    if (!this.db) await this.init();

    // Cleanup geoEvents
    try {
      results.geoEvents = await this.cleanupStoreByEndTime(this.GEO_EVENTS_STORE, now);
    } catch (error) {
      console.error('❌ [Cleanup] Erreur cleanup geoEvents:', error);
    }

    // Cleanup organizerEvents
    try {
      results.organizerEvents = await this.cleanupStoreByEndTime(this.ORGANIZER_EVENTS_STORE, now);
    } catch (error) {
      console.error('❌ [Cleanup] Erreur cleanup organizerEvents:', error);
    }

    // Cleanup favoriteEvents
    try {
      results.favoriteEvents = await this.cleanupStoreByEndTime(this.FAVORITE_EVENTS_STORE, now);
    } catch (error) {
      console.error('❌ [Cleanup] Erreur cleanup favoriteEvents:', error);
    }

    const totalDeleted = results.geoEvents + results.organizerEvents + results.favoriteEvents;
    if (totalDeleted > 0) {
      console.log(`🧹 [Cleanup] ${totalDeleted} événements expirés supprimés:`, results);
    }

    return results;
  }

  /**
   * Helper: Nettoie un store spécifique par endTime
   */
  private cleanupStoreByEndTime(storeName: string, now: string): Promise<number> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve(0);
        return;
      }

      try {
        const transaction = this.db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);

        // Vérifier si l'index endTime existe
        if (!store.indexNames.contains('endTime')) {
          console.log(`⚠️ [Cleanup] Store ${storeName} n'a pas d'index endTime, skip`);
          resolve(0);
          return;
        }

        const index = store.index('endTime');
        const range = IDBKeyRange.upperBound(now);
        const request = index.openCursor(range);
        let deletedCount = 0;

        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            resolve(deletedCount);
          }
        };

        request.onerror = () => {
          console.error(`❌ [Cleanup] Erreur cursor ${storeName}:`, request.error);
          reject(request.error);
        };
      } catch (error) {
        console.error(`❌ [Cleanup] Erreur transaction ${storeName}:`, error);
        resolve(0);
      }
    });
  }

  /**
   * 🕐 Démarre le scheduler de nettoyage automatique
   * - Cleanup après 30s au démarrage (pour ne pas bloquer l'init)
   * - Cleanup toutes les 6 heures
   */
  startCleanupScheduler(): void {
    const STARTUP_DELAY = 30000; // 30 secondes
    const CLEANUP_INTERVAL = 6 * 60 * 60 * 1000; // 6 heures

    // Cleanup au démarrage (après délai)
    // ⚠️ try/catch obligatoire : un rejet dans un callback setTimeout/setInterval
    // async ne remonte à AUCUN appelant → unhandledrejection direct dans Sentry
    // (cause de WHAT2DO-WEB-7 : init() rejette en UnknownError quand Safari a
    // supprimé la base, exactement 30s après l'arrivée du visiteur).
    setTimeout(async () => {
      try {
        console.log('🧹 [Cleanup] Démarrage du nettoyage automatique...');
        await this.cleanupExpiredEvents();
        await this.cleanupInvalidPublicOrganizers();
      } catch (error) {
        console.warn('⚠️ [Cleanup] Nettoyage au démarrage impossible (cache indisponible ?):', error);
      }
    }, STARTUP_DELAY);

    // Cleanup périodique
    setInterval(async () => {
      try {
        console.log('🧹 [Cleanup] Nettoyage périodique...');
        await this.cleanupExpiredEvents();
      } catch (error) {
        console.warn('⚠️ [Cleanup] Nettoyage périodique impossible (cache indisponible ?):', error);
      }
    }, CLEANUP_INTERVAL);

    console.log('✅ [Cleanup] Scheduler démarré (startup: 30s, interval: 6h)');
  }
}

// OPTIMISATION : Export du singleton
export const locationCache = LocationCacheService.getInstance();
